import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';

type AccountingOwner = {
  id: string;
  companyName: string;
  organizationNumber: string | null;
};

const ETAPPIO_ENTRY_TYPES = [
  'PLATFORM_FEE',
  'PLATFORM_FEE_VAT',
  'PLATFORM_FEE_REFUND',
  'PLATFORM_FEE_REFUND_VAT',
  'PLATFORM_FEE_REFUND_REVERSAL',
  'PLATFORM_FEE_REFUND_REVERSAL_VAT',
  'ADYEN_FEE',
  'PAYMENT_RECEIVED',
  'PAYMENT_REFUND',
  'PAYOUT_RELEASED',
  'RETENTION_RELEASED'
];

const SELLER_ENTRY_TYPES = [
  'SELLER_SALE',
  'SELLER_PLATFORM_FEE',
  'SELLER_PLATFORM_FEE_VAT',
  'SELLER_PAYOUT',
  'SELLER_RETENTION_PAYOUT',
  'BUYER_PRINCIPAL_LIABILITY',
  'BUYER_PRINCIPAL_SELLER_FEE',
  'BUYER_PRINCIPAL_MILESTONE_COMPLETION',
  'BUYER_PRINCIPAL_MILESTONE_COMPLETION_VAT',
  'BUYER_PRINCIPAL_MILESTONE_COMPLETION_REVERSAL',
  'BUYER_PRINCIPAL_MILESTONE_COMPLETION_REVERSAL_VAT',
  'BUYER_PRINCIPAL_REFUND',
  'BUYER_REFUND_REVERSAL',
  'BUYER_SELLER_FEE_REFUND',
  'BUYER_SELLER_FEE_REFUND_VAT',
  'BUYER_SELLER_FEE_REFUND_REVERSAL',
  'BUYER_SELLER_FEE_REFUND_REVERSAL_VAT',
  'BUYER_PRINCIPAL_OTHER',
  'BUYER_PRINCIPAL_OTHER_REVERSAL'
];

const fmt = (n: number) =>
  new Intl.NumberFormat('sv-SE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n);

const entrySruCode = (entry: any) => {
  const codes = [
    entry.debitSruCode ? `D ${entry.debitSruCode}` : null,
    entry.creditSruCode ? `K ${entry.creditSruCode}` : null
  ].filter(Boolean);
  return codes.length ? codes.join(' · ') : '—';
};

export function AdminAccounting() {
  const { t } = useTranslation();
  const [testFilter, setTestFilter] = useState<'all' | 'ordinary' | 'test' | 'unallocated'>('all');
  const [classificationSummary, setClassificationSummary] = useState<any>(null);
  const [entries, setEntries] = useState<any[]>([]);
  const [summary, setSummary] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [companies, setCompanies] = useState<AccountingOwner[]>([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [account, setAccount] = useState('');
  const [type, setType] = useState('');
  const [scope, setScope] = useState<'etappio' | 'seller'>('etappio');
  const [ownerCompanyId, setOwnerCompanyId] = useState('');
  const [tab, setTab] = useState<'entries' | 'summary' | 'chart'>('entries');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    adminApi
      .getBasAccounts()
      .then(setAccounts)
      .catch(() => {});
    adminApi
      .getAccountingOwners()
      .then(setCompanies)
      .catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setEntries([]);
    setSummary([]);
    setClassificationSummary(null);
    const params = {
      testFilter,
      scope,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      account: account || undefined,
      type: type || undefined,
      ownerCompanyId: scope === 'seller' ? ownerCompanyId || undefined : undefined
    };
    adminApi.getAccountingClassificationSummary(params).then(data => { if (!cancelled) setClassificationSummary(data); }).catch(() => {});
    adminApi
      .getAccountingEntries(params)
      .then(data => { if (!cancelled) setEntries(data); })
      .catch(() => {});
    adminApi
      .getAccountingSummary({
        testFilter,
        scope,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        ownerCompanyId: scope === 'seller' ? ownerCompanyId || undefined : undefined
      })
      .then(data => { if (!cancelled) setSummary(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [scope, fromDate, toDate, account, type, ownerCompanyId, testFilter]);

  const accountsByKind = useMemo(() => {
    const groups: Record<string, any[]> = {};
    for (const a of accounts) {
      if (!groups[a.kind]) groups[a.kind] = [];
      groups[a.kind].push(a);
    }
    Object.values(groups).forEach((g) => g.sort((a, b) => a.number.localeCompare(b.number)));
    return groups;
  }, [accounts]);

  const handleExport = async (format: 'csv' | 'sie4') => {
    try {
      setIsExporting(true);
      const { blob, filename } = await adminApi.exportAccounting({
        format,
        scope,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        account: account || undefined,
        type: type || undefined,
        ownerCompanyId: scope === 'seller' ? ownerCompanyId || undefined : undefined
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert(t('adminAccounting.errors.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  const totalDebit = entries.reduce((s, e) => s + (e.amountSek || 0), 0);
  const entryTypes = scope === 'etappio' ? ETAPPIO_ENTRY_TYPES : SELLER_ENTRY_TYPES;

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">
              {scope === 'etappio' ? t('adminAccounting.title.etappio') : t('adminAccounting.title.seller')} (BAS 2026)
            </h5>
          </div>
          <Breadcrumb
            items={[
              { label: t('adminAccounting.breadcrumb.home'), path: '/admin' },
              { label: t('adminAccounting.breadcrumb.current') }
            ]}
          />
        </div>
        <div className="page-header-right ms-auto d-flex gap-2">
          <button className="btn btn-outline-primary" onClick={() => handleExport('csv')} disabled={isExporting}>
            <i className="feather-download me-2"></i>
            {t('adminAccounting.actions.exportCsv')}
          </button>
          <button className="btn btn-primary" onClick={() => handleExport('sie4')} disabled={isExporting}>
            <i className="feather-download me-2"></i>
            {t('adminAccounting.actions.exportSie4')}
          </button>
        </div>
      </div>

      <Card className="mb-4">
        <label htmlFor="accounting-test-filter" className="form-label">{t('testProjects.accountingFilter')}</label>
        <select id="accounting-test-filter" className="form-select mb-2" value={testFilter} onChange={event => setTestFilter(event.target.value as typeof testFilter)}>
          {(['all', 'ordinary', 'test', 'unallocated'] as const).map(value => <option key={value} value={value}>{t(`testProjects.${value}`)}</option>)}
        </select>
        <p className="text-explain">{t('testProjects.exportAll')}</p>
        {classificationSummary && <div className="row g-3 mb-3">
          {(['ordinary', 'test', 'unallocated', 'total'] as const).map(group => {
            const value = group === 'total' ? classificationSummary.total : classificationSummary.groups[group];
            return <div key={group} className="col-12 col-sm-6 col-xl-3"><strong>{t(`testProjects.${group}`)}</strong><p className="mb-0">{t('testProjects.groupAmount', { count: value.count, amount: fmt(value.amountOre / 100) })}</p></div>;
          })}
        </div>}
        <div className="alert alert-light border small mb-3">
          {scope === 'etappio' ? t('adminAccounting.info.etappio') : t('adminAccounting.info.seller')}
        </div>
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label small text-muted mb-1">{t('adminAccounting.filters.scope')}</label>
            <select
              className="form-select form-select-sm"
              value={scope}
              onChange={(e) => {
                setScope(e.target.value as 'etappio' | 'seller');
                setType('');
                setOwnerCompanyId('');
              }}
            >
              <option value="etappio">{t('adminAccounting.filters.scopeEtappio')}</option>
              <option value="seller">{t('adminAccounting.filters.scopeSeller')}</option>
            </select>
            <div className="small mt-1">
              <span className={`badge ${scope === 'etappio' ? 'bg-primary' : 'bg-secondary'}`}>
                {scope === 'etappio'
                  ? t('adminAccounting.filters.badgeEtappio')
                  : t('adminAccounting.filters.badgeSeller')}
              </span>
            </div>
          </div>
          <div className="col-md-3">
            <label className="form-label small text-muted mb-1">{t('adminAccounting.filters.from')}</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small text-muted mb-1">{t('adminAccounting.filters.to')}</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small text-muted mb-1">{t('adminAccounting.filters.account')}</label>
            <select className="form-select form-select-sm" value={account} onChange={(e) => setAccount(e.target.value)}>
              <option value="">{t('adminAccounting.filters.allAccounts')}</option>
              {accounts.map((a) => (
                <option key={a.number} value={a.number}>
                  {a.number} — {a.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label small text-muted mb-1">{t('adminAccounting.filters.entryType')}</label>
            <select className="form-select form-select-sm" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">{t('adminAccounting.filters.allTypes')}</option>
              {entryTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          {scope === 'seller' && (
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1">{t('adminAccounting.filters.seller')}</label>
              <select
                className="form-select form-select-sm"
                value={ownerCompanyId}
                onChange={(e) => setOwnerCompanyId(e.target.value)}
              >
                <option value="">{t('adminAccounting.filters.allSellers')}</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.companyName} ({company.organizationNumber || t('adminAccounting.filters.orgNumberMissing')})
                  </option>
                ))}
              </select>
              {ownerCompanyId && (
                <Link
                  className="small d-inline-block mt-1"
                  to={`/accounting/company-report?companyId=${encodeURIComponent(ownerCompanyId)}`}
                >
                  {t('adminAccounting.actions.openCompanyReport')}
                </Link>
              )}
            </div>
          )}
        </div>
      </Card>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === 'entries' ? 'active' : ''}`} onClick={() => setTab('entries')}>
            {t('adminAccounting.tabs.entries', { count: entries.length })}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'summary' ? 'active' : ''}`} onClick={() => setTab('summary')}>
            {t('adminAccounting.tabs.summary')}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'chart' ? 'active' : ''}`} onClick={() => setTab('chart')}>
            {t('adminAccounting.tabs.chart', { count: accounts.length })}
          </button>
        </li>
      </ul>

      {tab === 'entries' && (
        <Card>
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t('adminAccounting.entriesTable.verification')}</th>
                  {scope === 'seller' && <th>{t('adminAccounting.entriesTable.seller')}</th>}
                  <th>{t('adminAccounting.entriesTable.date')}</th>
                  <th>{t('adminAccounting.entriesTable.type')}</th>
                  <th>{t('adminAccounting.entriesTable.debit')}</th>
                  <th>{t('adminAccounting.entriesTable.credit')}</th>
                  <th className="text-end">{t('adminAccounting.entriesTable.amount')}</th>
                  <th>{t('adminAccounting.entriesTable.sruCode')}</th>
                  <th>{t('adminAccounting.entriesTable.text')}</th>
                  <th>{t('adminAccounting.entriesTable.adyenRef')}</th>
                  <th>{t('adminAccounting.entriesTable.transaction')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={scope === 'seller' ? 11 : 10} className="text-center text-muted py-4">
                      <i className="feather-inbox fs-3 d-block mb-2"></i>
                      {t('adminAccounting.entriesTable.empty')}
                    </td>
                  </tr>
                )}
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="font-monospace small">{e.verificationNumber}</td>
                    {scope === 'seller' && (
                      <td className="small">
                        {companies.find((company) => company.id === e.ownerCompanyId)?.companyName ||
                          e.ownerCompanyId ||
                          '—'}
                      </td>
                    )}
                    <td className="small text-nowrap">{new Date(e.bookedAt).toLocaleDateString('sv-SE')}</td>
                    <td>
                      <span className="badge bg-light text-dark border">{e.entryType}</span>
                    </td>
                    <td className="small">
                      <span className="font-monospace">{e.debitAccount}</span>
                      <span className="text-muted d-block" style={{ fontSize: 10 }}>
                        {e.debitAccountName}
                      </span>
                    </td>
                    <td className="small">
                      <span className="font-monospace">{e.creditAccount}</span>
                      <span className="text-muted d-block" style={{ fontSize: 10 }}>
                        {e.creditAccountName}
                      </span>
                    </td>
                    <td className="text-end fw-semibold">{fmt(e.amountSek)}</td>
                    <td className="font-monospace small">{entrySruCode(e)}</td>
                    <td className="small">{e.description}
                      {e.classification && <div className="text-explain">{t(`testProjects.${e.classification}`)} · {t(`testProjects.reasons.${e.classificationReason}`)}</div>}
                      {e.classificationProjectId && <Link to={`/projects/${e.classificationProjectId}`}>{t('testProjects.openProject')}</Link>}
                    </td>
                    <td className="small font-monospace text-muted">{e.adyenReference || ''}</td>
                    <td>
                      {e.transactionId && (
                        <Link to={`/admin/transactions/${e.transactionId}`} className="small">
                          <i className="feather-external-link" style={{ fontSize: 12 }}></i>
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {entries.length > 0 && (
                <tfoot>
                  <tr className="table-light">
                    <td colSpan={scope === 'seller' ? 6 : 5} className="fw-bold">
                      {t('adminAccounting.entriesTable.total')}
                    </td>
                    <td className="text-end fw-bold">{fmt(totalDebit)}</td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>
      )}

      {tab === 'summary' && (
        <Card>
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t('adminAccounting.summaryTable.account')}</th>
                  <th>{t('adminAccounting.summaryTable.name')}</th>
                  <th className="text-end">{t('adminAccounting.summaryTable.debit')}</th>
                  <th className="text-end">{t('adminAccounting.summaryTable.credit')}</th>
                  <th className="text-end">{t('adminAccounting.summaryTable.balance')}</th>
                  <th className="text-end">{t('adminAccounting.summaryTable.count')}</th>
                </tr>
              </thead>
              <tbody>
                {summary.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      {t('adminAccounting.summaryTable.empty')}
                    </td>
                  </tr>
                )}
                {summary.map((s) => (
                  <tr key={s.account}>
                    <td className="font-monospace fw-semibold">{s.account}</td>
                    <td>{s.name}</td>
                    <td className="text-end">{fmt(s.debit)}</td>
                    <td className="text-end">{fmt(s.credit)}</td>
                    <td className={`text-end fw-bold ${s.net >= 0 ? 'text-success' : 'text-danger'}`}>{fmt(s.net)}</td>
                    <td className="text-end text-muted small">{s.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'chart' && (
        <div className="row g-4">
          {(['income', 'expense', 'asset', 'liability'] as const).map((kind) => (
            <div key={kind} className="col-md-6">
              <Card title={t(`adminAccounting.kind.${kind}`)}>
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 80 }}>{t('adminAccounting.chartTable.account')}</th>
                        <th>{t('adminAccounting.chartTable.name')}</th>
                        <th style={{ width: 90 }}>{t('adminAccounting.chartTable.sruCode')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(accountsByKind[kind] || []).map((a) => (
                        <tr key={a.number}>
                          <td className="font-monospace fw-semibold">{a.number}</td>
                          <td>
                            <div>{a.name}</div>
                            <small className="text-muted">{a.description}</small>
                          </td>
                          <td className="font-monospace">{a.sruCode || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
